import type { ItemStack, ItemStatus, PublishState, TacticApplication } from './cockpit-data';

// KET-1114 truth-in-UI.
//
// The defect: an Item Stacks row whose ledger status was `active` rendered as
// "Applied / ACTIVE / Under test: 96h" beside a Reverse button - an assertion
// that a live Amazon negation existed. Apply (W3) is ledger-only. Nothing
// reaches Amazon until the explicit manual publish leg (KET-1113) runs.
//
// Every publish-truth decision in the cockpit routes through this module so the
// table, the button labels and the timeline chips cannot drift apart.

export type PublishPresentation = {
  label: string;
  tone: 'live' | 'ledger' | 'reversed' | 'unknown';
  // Says the ledger/Amazon distinction in words, not just colour.
  title: string;
};

const PRESENTATION: Record<PublishState, PublishPresentation> = {
  published: {
    label: 'Live on Amazon',
    tone: 'live',
    title: 'Published: the negative keyword exists on Amazon and is blocking this query.',
  },
  not_published: {
    label: 'Ledger only',
    tone: 'ledger',
    title:
      'Applied in the ledger only. Nothing has reached Amazon: ads still serve on this query until you publish.',
  },
  reversed: {
    label: 'Reversed',
    tone: 'reversed',
    title:
      'Was published, then reversed: the Amazon negative was archived and the ledger row retired. Ads can serve again.',
  },
  unknown: {
    label: 'Publish state unknown',
    tone: 'unknown',
    title:
      'The publish journal could not be read, so whether this reached Amazon is unknown. It is not a confirmation that nothing was published.',
  },
};

// KET-2764: a tactic was applied here and every application has since been
// retired. Recessive tone -- nothing is wrong, there is simply nothing in force.
// Kept OUT of PRESENTATION because it is not a PublishState: the genuine
// `unknown` (unreadable publish journal) must stay distinguishable from it.
export const NOTHING_IN_FORCE: PublishPresentation = {
  label: 'Nothing in force',
  tone: 'ledger',
  title:
    'A tactic was applied here and has since been retired. Nothing is in effect on Amazon now. See the Change Log for the history.',
};

export function publishPresentation(state: PublishState): PublishPresentation {
  return PRESENTATION[state] ?? PRESENTATION.unknown;
}

// A retired application that was never published only ever retired a ledger row.
// Saying "Reversed" there would claim an Amazon mutation that never happened.
export function applicationPublishState(application: TacticApplication): PublishState {
  return application.publish_state;
}

// The row-level headline. Priority is "what is true on Amazon right now":
// anything live dominates, then a real reversal, then an unreadable journal,
// and only then the ledger-only default.
export function rowPublishState(row: ItemStack): PublishState | null {
  const applications = row.application_timeline;
  if (applications.length === 0) return null;
  const states = new Set(applications.map(applicationPublishState));
  if (states.has('published')) return 'published';
  if (states.has('reversed')) return 'reversed';
  if (states.has('unknown')) return 'unknown';
  return 'not_published';
}

// The application the row's action buttons operate on. The timeline is ordered
// newest-first, so the plain "newest live application" default would let a
// newer LEDGER-ONLY application hide an older application that is actually live
// on Amazon: the row would offer "Retire ledger entry" while a real negative
// stayed unreachable. Anything published wins the button.
export function actionableApplication(row: ItemStack): TacticApplication | null {
  const live = row.application_timeline.filter(
    (application) => application.current_status !== 'retired' && isReversible(application),
  );
  return live.find((application) => application.publish_state === 'published') ?? live[0] ?? null;
}

export function actionableStatusApplication(row: ItemStatus): TacticApplication | null {
  const live = row.active_stack.filter(
    (application) => application.current_status !== 'retired' && isReversible(application),
  );
  return live.find((application) => application.publish_state === 'published') ?? live[0] ?? null;
}

export type ReverseAffordance = {
  // `reverse` archives the Amazon negative AND retires the ledger row;
  // `retire` only retires the ledger row, because there is nothing on Amazon.
  kind: 'reverse' | 'retire';
  label: string;
  disabled: boolean;
  title: string;
};

// v1 backend reversal supports keyword negations only; keep the button off other
// tactic types instead of surfacing a 400 after the click.
export function isReversible(application: TacticApplication): boolean {
  return /keyword[-_]negation|negate/.test(application.tactic_id);
}

// Retire is off until bug 40c0402f-c6c9-8182 is fixed: it calls the live reversal, which can archive an Amazon negative. To turn it back on, set this to true.
const RETIRE_ENABLED = false;

/** What Retire means right now, for the line beside the button and the legend. */
export function retireNote(): string {
  return RETIRE_ENABLED
    ? 'Nothing changes on Amazon.'
    : 'Retire is off for now: it could archive a live Amazon negative.';
}

export function reverseAffordance(
  application: TacticApplication,
  { keyword, market }: { keyword: string; market: string },
): ReverseAffordance {
  const scope = `"${keyword}" (${market})`;
  if (!isReversible(application)) {
    return {
      kind: 'reverse',
      label: 'Reverse',
      disabled: true,
      title: 'Reversal is not yet supported for this tactic type.',
    };
  }
  if (application.publish_state === 'not_published') {
    return {
      kind: 'retire',
      label: 'Retire ledger entry',
      disabled: !RETIRE_ENABLED,
      title: RETIRE_ENABLED
        ? `Nothing to reverse on Amazon: ${application.tactic_name} on ${scope} was never published. This retires the ledger entry only.`
        : retireNote(),
    };
  }
  if (application.publish_state === 'reversed') {
    return {
      kind: 'reverse',
      label: 'Reverse',
      disabled: true,
      title: `Already reversed: the Amazon negative for ${scope} was archived and the ledger row retired.`,
    };
  }
  return {
    kind: 'reverse',
    label: 'Reverse',
    disabled: false,
    title:
      application.publish_state === 'unknown'
        ? `Publish state unknown for ${scope}. Reverse archives any live Amazon negative and retires the ledger application.`
        : `Reverse ${application.tactic_name} on ${scope}: archives the Amazon negative and retires the ledger application.`,
  };
}

export type PublishAffordance = {
  label: string;
  disabled: boolean;
  title: string;
};

export function publishAffordance(
  application: TacticApplication,
  { keyword, market }: { keyword: string; market: string },
): PublishAffordance {
  const scope = `"${keyword}" (${market})`;
  if (application.publish_state === 'published') {
    return {
      label: 'Published',
      disabled: true,
      // The target_ref is the created Amazon keyword id, the receipt.
      title: application.publish_target_ref
        ? `Already live on Amazon (target_ref ${application.publish_target_ref}).`
        : 'Already live on Amazon.',
    };
  }
  if (application.publish_state === 'reversed') {
    return {
      label: 'Publish',
      disabled: true,
      title: `Reversed: re-apply the tactic before publishing ${scope} again.`,
    };
  }
  return {
    label: 'Publish',
    disabled: false,
    title: `Publish ${application.tactic_name} on ${scope}: puts the negative keyword LIVE on Amazon. Apply alone changes nothing on Amazon; this is the explicit publish gate.`,
  };
}

// The trial clock ("Under test: 96h") only means something once the mutation is
// live on Amazon. On a never-published application it is a fabricated
// measurement of an experiment that never started.
export function experimentsAreMeaningful(application: TacticApplication): boolean {
  return application.publish_state !== 'not_published';
}
